/**
 * Utility functions for detecting and processing URLs in text
 */

/**
 * Regular expression to detect URLs in text
 * Matches:
 * - http://example.com
 * - https://example.com  
 * - www.example.com
 * - example.com (with common TLDs)
 */
const URL_REGEX = /((?:https?:\/\/)?(?:www\.)?[a-zA-Z0-9-]+\.(?:[a-zA-Z]{2,}|[a-zA-Z]{2,}\.[a-zA-Z]{2,})(?:\/[^\s]*)?)/gi;

/**
 * Detects if a URL needs a protocol prefix
 * @param {string} url - The URL to check
 * @returns {string} - The URL with proper protocol
 */
export const ensureProtocol = (url) => {
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  return `https://${url}`;
};

/**
 * Finds all URLs in a text string
 * @param {string} text - The text to search for URLs
 * @returns {Array} - Array of objects with url, start, and end positions
 */
export const findUrls = (text) => {
  if (!text) return [];
  
  const urls = [];
  let match;
  
  // Reset regex state
  URL_REGEX.lastIndex = 0;
  
  while ((match = URL_REGEX.exec(text)) !== null) {
    urls.push({
      url: match[1],
      start: match.index,
      end: match.index + match[1].length
    });
  }
  
  return urls;
};

/**
 * Splits text into segments, separating URLs from regular text
 * @param {string} text - The text to process
 * @returns {Array} - Array of objects with type ('text' or 'url') and content
 */
export const parseTextWithUrls = (text) => {
  if (!text) return [{ type: 'text', content: '' }];
  
  const urls = findUrls(text);
  
  if (urls.length === 0) {
    return [{ type: 'text', content: text }];
  }
  
  const segments = [];
  let lastEnd = 0;
  
  urls.forEach(({ url, start, end }) => {
    // Add text before URL
    if (start > lastEnd) {
      segments.push({
        type: 'text',
        content: text.substring(lastEnd, start)
      });
    }
    
    // Add URL
    segments.push({
      type: 'url',
      content: url,
      href: ensureProtocol(url)
    });
    
    lastEnd = end;
  });
  
  // Add remaining text after last URL
  if (lastEnd < text.length) {
    segments.push({
      type: 'text',
      content: text.substring(lastEnd)
    });
  }
  
  return segments;
};